import { apiClient } from './ApiClient'
import {
  SlaAnalyticsResponse,
  EscalationAnalyticsResponse
} from '../types/entities'

export interface AdminDashboardStats {
  totalTickets: number
  openTickets: number
  resolvedToday: number
  responseTime: string
  totalUsers: number
  activeAgents: number
  totalOrganizations: number
  setupComplete: boolean
}

export interface RecentActivity {
  id: string
  type: string
  message: string
  timestamp: string
  status: string
}

export class AnalyticsService {
  /**
   * Get admin dashboard statistics
   */
  static async getAdminDashboard(): Promise<AdminDashboardStats> {
    return apiClient.get<AdminDashboardStats>('/analytics/admin/dashboard')
  }

  /**
   * Get recent activity for admin dashboard
   */
  static async getRecentActivity(): Promise<RecentActivity[]> {
    return apiClient.get<RecentActivity[]>('/analytics/admin/recent-activity')
  }

  /**
   * Get ticket analytics for a date range
   */
  static async getTicketAnalytics(
    startDate: string,
    endDate: string,
    organizationId?: string,
    childSiteId?: string
  ): Promise<any> {
    const params = new URLSearchParams({
      startDate,
      endDate
    })
    if (organizationId) params.append('organizationId', organizationId)
    if (childSiteId) params.append('childSiteId', childSiteId)
    return apiClient.get<any>(`/analytics/tickets?${params}`)
  }

  /**
   * Get agent performance metrics
   */
  static async getAgentPerformance(
    startDate: string,
    endDate: string,
    organizationId?: string,
    childSiteId?: string
  ): Promise<any> {
    const params = new URLSearchParams({
      startDate,
      endDate
    })
    if (organizationId) params.append('organizationId', organizationId)
    if (childSiteId) params.append('childSiteId', childSiteId)
    return apiClient.get<any>(`/analytics/agents?${params}`)
  }

  /**
   * Sprint 3: Get comprehensive SLA analytics including at-risk tickets and breach trends
   */
  static async getSlaAnalytics(
    startDate?: string,
    endDate?: string
  ): Promise<SlaAnalyticsResponse> {
    const params = new URLSearchParams()
    if (startDate) params.append('startDate', startDate)
    if (endDate) params.append('endDate', endDate)
    const query = params.toString()
    return apiClient.get<SlaAnalyticsResponse>(`/analytics/sla${query ? `?${query}` : ''}`)
  }

  /**
   * Sprint 3: Get comprehensive escalation analytics including frequency, categories, and path usage
   */
  static async getEscalationAnalytics(
    startDate?: string,
    endDate?: string
  ): Promise<EscalationAnalyticsResponse> {
    const params = new URLSearchParams()
    if (startDate) params.append('startDate', startDate)
    if (endDate) params.append('endDate', endDate)
    const query = params.toString()
    return apiClient.get<EscalationAnalyticsResponse>(`/analytics/escalations${query ? `?${query}` : ''}`)
  }
}
